<script lang="ts">
  import { faEnvelope, faPhoneRotary } from '@fortawesome/pro-regular-svg-icons';
  import { faInstagram, faLinkedinIn, faTwitter } from '@fortawesome/free-brands-svg-icons';
  import { hyphenateHtmlSync, hyphenateTextSync } from '$lib/utils/hyphens.js';
  import EyeCatcher from '$lib/components/EyeCatcher.svelte';
  import Fa from 'svelte-fa';
  import type { PageData } from './$types.js';
  import Section from '$lib/components/Section.svelte';
  import Sidebar from '$lib/components/sidebars/Sidebar.svelte';
  import { mdToHtml } from '$lib/utils/mdToHtml.js';

  export let data: PageData;

  $: hasSocial = [
    data.employee.emailAddress,
    data.employee.phoneNumber,
    data.employee.twitterUrl,
    data.employee.linkedInUrl,
    data.employee.instagramUrl
  ].some((value) => typeof value !== 'undefined');
</script>

{#if data.employee.eyeCatcher}
  <EyeCatcher {...data.employee.eyeCatcher} employee={true} />
{/if}

<div class="fixed-width grid two-columns">
  <main>
    <Section headline={data.employee.name}>
      {#if data.employee.personal}
        <!-- eslint-disable-next-line svelte/no-at-html-tags -->
        {@html mdToHtml(data.employee.personal)}
      {/if}
    </Section>
    {#if data.employee.areasOfLaw}
      <Section subHeadline="Rechtsgebieden">
        <!-- eslint-disable-next-line svelte/no-at-html-tags -->
        {@html mdToHtml(data.employee.areasOfLaw)}
      </Section>
    {/if}
  </main>
  <aside>
    <Sidebar>
      <h2>{hyphenateTextSync(data.employee.jobTitle)}</h2>
      <p>{hyphenateTextSync(data.employee.title)}</p>
      {#if data.employee.summary}
        <!-- eslint-disable-next-line svelte/no-at-html-tags -->
        {@html mdToHtml(data.employee.summary)}
      {/if}
    </Sidebar>
    {#if hasSocial}
      <Section class="social-links">
        <!-- Email address -->
        {#if data.employee.emailAddress}
          <a
            href="mailto:{data.employee.emailAddress}"
            rel="noreferrer"
            title="Stuur een e-mail naar {data.employee.name} op {data.employee.emailAddress}"
            ><Fa icon={faEnvelope} fw={true} /></a>
        {/if}
        <!-- Phone number -->
        {#if data.employee.phoneNumber}
          <a
            href="tel:{data.employee.phoneNumber}"
            rel="noreferrer"
            title="Bel naar {data.employee.name} op {data.employee.phoneNumber}"
            ><Fa icon={faPhoneRotary} fw={true} /></a>
        {/if}
        <!-- LinkedIn -->
        {#if data.employee.linkedInUrl}
          <a
            href={data.employee.linkedInUrl}
            target="_blank"
            rel="noreferrer"
            title="Ga naar de LinkedIn pagina van {data.employee.name}"
            ><Fa icon={faLinkedinIn} fw={true} /></a>
        {/if}
        <!-- Instagram -->
        {#if data.employee.instagramUrl}
          <a
            href={data.employee.instagramUrl}
            target="_blank"
            rel="noreferrer"
            title="Ga naar de Instagram pagina van {data.employee.name}"
            ><Fa icon={faInstagram} fw={true} /></a>
        {/if}
        <!-- Twitter -->
        {#if data.employee.twitterUrl}
          <a
            href={data.employee.twitterUrl}
            target="_blank"
            rel="noreferrer"
            title="Ga naar de Twitter pagina van {data.employee.name}"
            ><Fa icon={faTwitter} fw={true} /></a>
        {/if}
      </Section>
    {/if}
    {#if data.employee.memberships}
      <Section class="memberships">
        <div>
          <!-- eslint-disable-next-line svelte/no-at-html-tags -->
          {@html hyphenateHtmlSync(data.employee.memberships)}
        </div>
      </Section>
    {/if}
  </aside>
</div>

<style lang="scss">
  @use '$lib/styles/variables';
  @use '$lib/styles/templates/twoColumnsPage';

  aside {
    // Social links
    > :global(.social-links) {
      display: flex !important;
      flex-wrap: wrap;
      justify-content: space-evenly;
      gap: 0.7em;

      > :global(a) {
        aspect-ratio: 1 / 1;
        width: auto;
        height: auto;

        &:hover,
        &:focus {
          > :global(svg) {
            background-color: variables.$color-blue;
          }
        }

        :global(svg) {
          background-color: var(--color-purple);
          border-radius: 100%;
          width: 3em !important;
          height: 3em !important;
          padding: 0.55em;
          color: #fff;
          display: inline-block;
          margin: 0 auto;
          transition: background-color 0.15s linear;
        }
      }
    }

    // Memberships
    > :global(.memberships > div) {
      margin: 0 auto;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: variables.$section-padding;
      width: 70%;

      :global(a) {
        display: flex;
        gap: variables.$section-padding;
        flex-direction: column;
        align-items: center;
      }

      :global(img) {
        width: auto;
        height: auto;
        max-width: 100%;
        max-height: 77px;
        object-fit: contain;
      }
    }
  }
</style>
